import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useEffect, useState } from 'react'

import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'
import type { IChecklistStep, IChecklistTask } from '~/types/checklist'
import type { IUserPublic } from '~/types/user'

import { checklistsKeys, useNotifyStatusChangeMutation } from '../../queries'

import Button from '~/components/Buttons/Button'
import CollapsibleCard from '~/components/CollapsibleCard'
import { MILLISECONDS_IN_NANO } from '~/const'
import { useGetMeAsUserQuery, useUserDetailsQueries, useUserDetailsQuery } from '~/queries/users'
import { CHECKLIST_STATUS } from '~/types/checklist'
import { getTeamNameMapping } from '~/utils/team'
import { displayToast } from '~/utils/toast'

import './styles.scss'

type BasicUserInfo = {
  date: number
  id: string
  job: string
  name: string
  picture?: string
}

const NodeTemplate = ({
  meId,
  onResetToggle,
  task,
  teams,
  toggleAll,
}: {
  meId: string
  onResetToggle: () => void
  task: IChecklistTask
  teams: Record<string, string[]>
  toggleAll?: boolean
}): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { assignation_key, checklist_id, description, id, status, title, updated_at_ts, updated_by } = task

  const [isOpen, setIsOpen] = useState(status === CHECKLIST_STATUS.IN_PROGRESS)

  const { mutateAsync: notifyStatusChangeMutation } = useNotifyStatusChangeMutation()

  const teamIds = teams[assignation_key]

  const formatUser = ({ name, picture }: IUserPublic) => ({
    date: formatDate(updated_at_ts / MILLISECONDS_IN_NANO, DATE_FORMATS.DEFAULT_WITH_HOURS),
    name,
    picture,
  })

  const formatUsersQueries = ({ name, picture, user_id }: IUserPublic) => ({
    id: user_id,
    name,
    picture,
  })

  /** Queries & Mutations */

  const userQueries = useUserDetailsQueries(teamIds, formatUsersQueries)

  const teamMembers = userQueries.some((e) => e.isLoading)
    ? []
    : userQueries.map(({ data }) => data as BasicUserInfo).filter((value) => value)

  const { data: updatedBy } = useUserDetailsQuery(updated_by, !!updated_by, formatUser)

  /** Effects */

  useEffect(() => {
    if (toggleAll !== undefined) {
      setIsOpen(!toggleAll)
    }
  }, [toggleAll])

  useEffect(() => {
    setIsOpen(status === CHECKLIST_STATUS.IN_PROGRESS)
  }, [status])

  /** Handlers */

  const handleSkipTask = async () => {
    try {
      await notifyStatusChangeMutation({ checklistId: checklist_id, status: CHECKLIST_STATUS.SKIPPED, taskId: id })
      queryClient.invalidateQueries({ queryKey: checklistsKeys.details(checklist_id) })
      displayToast('Task successfully skipped', 'success')
    } catch (error) {
      if ((error as IApiError).message.includes('is not assigned to this task')) {
        displayToast('Task could not be skipped; you are not assigned to it', 'error')
      } else {
        displayToast('Task could not be skipped', 'error')
      }
    }
  }

  const handleDoneTask = async () => {
    try {
      await notifyStatusChangeMutation({ checklistId: checklist_id, status: CHECKLIST_STATUS.DONE, taskId: id })
      queryClient.invalidateQueries({ queryKey: checklistsKeys.details(checklist_id) })
      displayToast('Task successfully done', 'success')
    } catch (error) {
      if ((error as IApiError).message.includes('is not assigned to this task')) {
        displayToast('Task could not be done; you are not assigned to it', 'error')
      } else {
        displayToast('Task could not be done', 'error')
      }
    }
  }

  const handleToggle = () => {
    setIsOpen(!isOpen)
    onResetToggle()
  }

  /** Render */

  return (
    <CollapsibleCard
      belongsToTeam={teamMembers.some(({ id: userId }) => userId === meId)}
      current={status === CHECKLIST_STATUS.IN_PROGRESS}
      description={description}
      isDone={status === CHECKLIST_STATUS.DONE}
      isOpen={isOpen}
      isSkipped={status === CHECKLIST_STATUS.SKIPPED}
      name={title}
      onDone={handleDoneTask}
      onSkip={handleSkipTask}
      onToggle={handleToggle}
      teamName={getTeamNameMapping(assignation_key)}
      updatedBy={updatedBy}
      users={teamMembers}
    />
  )
}

interface Props {
  steps: IChecklistStep[]
  teams: Record<string, string[]>
}

const OrganisationChart = ({ steps, teams }: Props): ReactElement | null => {
  /** Local state */

  const [toggleAll, setToggleAll] = useState<boolean>()

  /** Queries & Mutations */

  const { data: user, isLoading: profileLoading } = useGetMeAsUserQuery()

  /** Handlers */

  const handleCollapseAll = (value: boolean) => () => setToggleAll(value)

  const handleResetToggle = () => setToggleAll(undefined)

  return !profileLoading && user ? (
    <>
      <div className="organization-chart-actions">
        <Button ghost onClick={handleCollapseAll(false)} size="small">
          Expand all
        </Button>
        <Button ghost onClick={handleCollapseAll(true)} size="small">
          Collapse all
        </Button>
      </div>
      <div className="steps-container">
        {steps.map(({ pipelines }, stepIdx, steps) => {
          const maxPipelines =
            stepIdx + 1 < steps.length
              ? Math.max(steps[stepIdx + 1].pipelines.length, steps[stepIdx].pipelines.length)
              : steps[stepIdx].pipelines.length

          return (
            <div className="step-container" key={stepIdx}>
              <div className="pipelines-container">
                {pipelines.map(({ tasks }, pipelineIdx, pipelines) => (
                  <div
                    className={classNames('pipeline-container', {
                      'no-margin': pipelines.length > 1 && pipelineIdx === pipelines.length - 1,
                      'with-left-sep': pipelines.length > 1 && pipelineIdx === pipelines.length - 1,
                      'with-right-sep': pipelines.length > 1 && pipelineIdx === 0,
                    })}
                    key={pipelineIdx}
                  >
                    {tasks.map((task) => (
                      <div className={classNames('task-container', { 'with-separator': stepIdx > 0 })} key={task.id}>
                        <NodeTemplate
                          meId={user.user_id}
                          onResetToggle={handleResetToggle}
                          task={task}
                          teams={teams}
                          toggleAll={toggleAll}
                        />
                      </div>
                    ))}
                  </div>
                ))}
                {stepIdx < steps.length - 1 &&
                ((stepIdx + 1 < steps.length && steps[stepIdx + 1].pipelines.length > 1) ||
                  (stepIdx + 1 < steps.length && steps[stepIdx].pipelines.length > 1)) ? (
                  <div className="links-container">
                    <div
                      className="tasks-link"
                      style={{
                        width: `${
                          (maxPipelines - 1) * (100 / maxPipelines) +
                          (steps[stepIdx + 1].pipelines.length > steps[stepIdx].pipelines.length &&
                          steps[stepIdx + 1].pipelines.length % 2 === 1
                            ? maxPipelines / 2
                            : 0)
                        }%`,
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </>
  ) : null
}

export default OrganisationChart
